import { StandardEvents } from '@shopify/events';

/**
 * Pre-checkout upsell (snippets/wt-checkout-upsell.liquid).
 *
 * Intercepts clicks on checkout buttons (cart drawer and cart page). Unless the offered product is
 * already in the cart or the shopper declined it this session, a modal offers it first:
 * - "Yes, add it" adds the product via the Ajax Cart API and then continues to checkout (one click).
 * - "No thanks" continues to checkout and remembers the decision for this session.
 * - Close / Escape / backdrop returns to the cart without checking out. With "once per session" on it
 *   also counts as a decline, so the next checkout click goes straight through.
 * Whether the product is already in the cart is rendered by Liquid (data-in-cart) and kept current from
 * the cart update events, so the click is decided without waiting for /cart.js. Only when that state is
 * unknown (an update still in flight, a page restored from the back/forward cache) is the cart fetched,
 * with aria-busy on the pressed button meanwhile.
 * Without an added item, continuing re-clicks the original button with a bypass flag, so the theme's
 * own checkout behaviour is preserved. After adding, it goes straight to /checkout instead: the cart
 * form posts positional `updates[]` quantities, which would no longer line up with the new cart lines.
 * When the offered product closes the gap to the first cart reward (Bonus Build), the hidden line
 * [data-upsell-unlock] is shown. The gap uses the same basis as snippets/wt-cart-progress.liquid.
 * The line stays hidden while a sale campaign runs or the cart carries a discount (wt-reward-claims).
 * A checkout click from the cart drawer or cart page skips the modal while that cart already makes its
 * one offer (inline Mystery Box card or gap-filler kit, or the inline card setting is on).
 * The dialog image is lazy in Liquid (the snippet renders on every page); it is switched to eager once
 * the cart has items, so the popup doesn't open with an empty image box.
 */
const CHECKOUT_SELECTOR = 'button[name="checkout"], input[name="checkout"], a[href$="/checkout"]';
const DECLINED_KEY = 'wt-upsell-declined';

/**
 * Same rule as snippets/wt-is-gift-card.liquid: native gift cards and the imported gift card product.
 * @param {any} item - cart.js line item
 */
const isGiftCard = (item) => {
  const type = String(item.product_type ?? '').trim().toLowerCase();
  return Boolean(item.gift_card) || type === 'gift card' || type === 'gift cards' || String(item.handle ?? '').includes('gift-card');
};

/**
 * True when the cart carries any discount (automatic sale, code): then an added item may count less
 * than its price towards the reward, so no unlock claim is made (snippets/wt-reward-claims.liquid).
 * Discounted bonus-build lines don't count (they may be made free by a discount).
 * @param {any} cart - cart.js object
 * @param {number[]} rewardVariants - variant ids of the bonus builds
 */
const hasDiscount = (cart, rewardVariants) =>
  (Array.isArray(cart?.cart_level_discount_applications) && cart.cart_level_discount_applications.length > 0) ||
  (Array.isArray(cart?.items) &&
    cart.items.some(
      (/** @type {any} */ item) =>
        !rewardVariants.includes(Number(item.variant_id ?? item.id)) &&
        (Number(item.final_line_price ?? 0) < Number(item.original_line_price ?? 0) ||
          (Array.isArray(item.line_level_discount_allocations) && item.line_level_discount_allocations.length > 0))
    ));

/**
 * Cart total that counts towards a reward: no gift cards, no bonus-build lines (cents).
 * @param {any[]} items - cart.js line items
 * @param {number[]} rewardVariants - variant ids of the bonus builds
 */
const rewardTotal = (items, rewardVariants) =>
  items
    .filter((item) => !rewardVariants.includes(Number(item.variant_id ?? item.id)) && !isGiftCard(item))
    .reduce((sum, item) => sum + Number(item.final_line_price || 0), 0);

class WtCheckoutUpsell extends HTMLElement {
  /** @type {HTMLElement | null} */
  pendingButton = null;
  bypass = false;
  /** @type {boolean | null} Offered product in the cart: known (true/false) or null = fetch /cart.js */
  offerInCart = null;
  /** @type {boolean | null} Cart holds only gift cards (no offer): known (true/false) or null = fetch /cart.js */
  allGiftCards = null;
  /** @type {number | null} Cart total towards reward 1 in cents, or null = fetch /cart.js */
  rewardTotal = null;
  /** Cart carries a discount (live state from cart events / cart.js) */
  cartDiscounted = false;
  #cartUpdates = 0;
  #prewarmed = false;

  connectedCallback() {
    this.dialog = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-dialog]'));
    this.offerInCart = this.dataset.inCart === 'true' ? true : this.dataset.inCart === 'false' ? false : null;
    this.allGiftCards =
      this.dataset.allGiftCards === 'true' ? true : this.dataset.allGiftCards === 'false' ? false : null;
    this.rewardTotal = this.dataset.rewardTotal ? Number(this.dataset.rewardTotal) : null;
    if (this.dataset.inCart === 'false' && Number(this.dataset.itemCount || 0) > 0) this.#prewarm();
    document.addEventListener('click', this.#interceptCheckout, true);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    window.addEventListener('pageshow', this.#handlePageShow);
    this.addEventListener('click', this.#handleClick);
    // Escape: close without checking out, remembered like the close button
    this.dialog?.addEventListener('cancel', () => this.#rememberDismiss());
    this.dialog?.addEventListener('close', () => {
      this.querySelector('[data-error]')?.setAttribute('hidden', '');
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#interceptCheckout, true);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    window.removeEventListener('pageshow', this.#handlePageShow);
    this.removeEventListener('click', this.#handleClick);
  }

  /** @param {Event} event */
  #handleCartUpdate = (event) => {
    // Unknown until this update settles; a checkout click in between falls back to /cart.js.
    const update = ++this.#cartUpdates;
    this.offerInCart = null;
    this.allGiftCards = null;
    this.rewardTotal = null;
    this.#prewarm();
    /** @type {Promise<any> | undefined} */
    const promise = /** @type {any} */ (event).promise;
    promise
      ?.then(({ detail } = {}) => {
        if (update !== this.#cartUpdates || !Array.isArray(detail?.items)) return;
        this.offerInCart = detail.items.some((/** @type {any} */ item) => String(item.product_id) === this.dataset.productId);
        this.allGiftCards = detail.items.length > 0 && detail.items.every(isGiftCard);
        this.rewardTotal = rewardTotal(detail.items, this.#rewardVariants());
        this.cartDiscounted = hasDiscount(detail, this.#rewardVariants());
      })
      .catch(() => {});
  };

  /** @param {PageTransitionEvent} event */
  #handlePageShow = (event) => {
    if (event.persisted) {
      this.offerInCart = null;
      this.allGiftCards = null;
      this.rewardTotal = null;
    }
  };

  /** Load the dialog image ahead of the first checkout click (once). */
  #prewarm() {
    if (this.#prewarmed) return;
    this.#prewarmed = true;
    for (const img of this.querySelectorAll('img[loading="lazy"]')) {
      if (img instanceof HTMLImageElement) img.loading = 'eager';
    }
  }

  /** @returns {number[]} */
  #rewardVariants() {
    return (this.dataset.rewardVariants ?? '')
      .split(',')
      .map(Number)
      .filter((id) => id > 0);
  }

  /**
   * Show the unlock line only when the offer really closes the gap: offer price >= gap > 0, and never
   * while a sale campaign runs or the cart carries a discount (the offer could count less than its price).
   */
  #updateUnlockLine() {
    const line = this.querySelector('[data-upsell-unlock]');
    if (!(line instanceof HTMLElement)) return;
    if (this.dataset.unlockClaims === 'off' || this.cartDiscounted) {
      line.hidden = true;
      return;
    }
    const threshold = Number(this.dataset.rewardThreshold || 0);
    const price = Number(this.dataset.offerPrice || 0);
    const gap = threshold > 0 && this.rewardTotal !== null ? threshold - this.rewardTotal : 0;
    line.hidden = !(gap > 0 && price >= gap);
  }

  /** Close / Escape / backdrop: counts as a decline for the session when "once per session" is on. */
  #rememberDismiss() {
    if (this.dataset.oncePerSession === 'true') this.#rememberDecline();
  }

  #declined() {
    try {
      return sessionStorage.getItem(DECLINED_KEY) === '1';
    } catch (error) {
      return false;
    }
  }

  #rememberDecline() {
    try {
      sessionStorage.setItem(DECLINED_KEY, '1');
    } catch (error) {
      /* storage blocked – the offer simply shows again */
    }
  }

  /** @param {MouseEvent} event */
  #interceptCheckout = async (event) => {
    if (this.bypass || !this.dialog) return;
    const target = event.target instanceof Element ? event.target.closest(CHECKOUT_SELECTOR) : null;
    if (!(target instanceof HTMLElement) || target.closest('wt-checkout-upsell')) return;
    if (target instanceof HTMLButtonElement && target.disabled) return;
    if (this.dataset.oncePerSession === 'true' && this.#declined()) return;
    if (this.#cartShowsOffer(target)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.pendingButton = target;

    let inCart = this.offerInCart;
    if (inCart === null || this.allGiftCards === null || this.rewardTotal === null) {
      // Visible feedback while the cart is checked
      target.setAttribute('aria-busy', 'true');
      try {
        inCart = await this.#inCart();
      } finally {
        target.removeAttribute('aria-busy');
      }
    }

    // Gift-card-only carts are digital: no physical add-on is offered
    if (inCart || this.allGiftCards) {
      this.#continue();
      return;
    }
    this.#updateUnlockLine();
    this.dialog.showModal();
    /** @type {HTMLElement | null} */ (this.querySelector('[data-add]'))?.focus();
  };

  /**
   * The cart drawer or cart page the click comes from already made its one offer: the inline Mystery
   * Box card (snippets/wt-cart-mystery.liquid) or a gap-filler kit (snippets/wt-cart-gap-fill.liquid).
   * With the inline card switched on, it replaces this modal for every checkout click from the cart.
   * Then the modal would ask a second time, or contradict the kit just recommended, so it is skipped.
   * Only with Theme settings → "One cart offer" on (data-one-offer); off keeps card and popup as before.
   * @param {HTMLElement} target - the pressed checkout button
   */
  #cartShowsOffer(target) {
    if (this.dataset.oneOffer !== 'true') return false;
    const cartScope = target.closest('#cart-drawer, .cart-page');
    if (!cartScope) return false;
    if (this.dataset.inlineOffer === 'true') return true;
    return Boolean(cartScope.querySelector('.wt-cart-gap, [id^="WtCartMystery-"]'));
  }

  async #inCart() {
    // A cart change while this request runs makes its answer stale; #handleCartUpdate then owns the state
    const update = this.#cartUpdates;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const cart = await response.json();
      const inCart =
        Array.isArray(cart.items) && cart.items.some((/** @type {any} */ item) => String(item.product_id) === this.dataset.productId);
      if (update === this.#cartUpdates) {
        this.offerInCart = inCart;
        this.allGiftCards = Array.isArray(cart.items) && cart.items.length > 0 && cart.items.every(isGiftCard);
        this.rewardTotal = Array.isArray(cart.items) ? rewardTotal(cart.items, this.#rewardVariants()) : null;
        this.cartDiscounted = hasDiscount(cart, this.#rewardVariants());
      }
      return inCart;
    } catch (error) {
      return false;
    }
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || !this.dialog?.open) return;

    if (target === this.dialog) {
      // Click on the backdrop
      this.#rememberDismiss();
      this.dialog.close();
    } else if (target.closest('[data-close]')) {
      this.#rememberDismiss();
      this.dialog.close();
    } else if (target.closest('[data-decline]')) {
      if (this.dataset.oncePerSession === 'true') this.#rememberDecline();
      this.dialog.close();
      this.#continue();
    } else if (target.closest('[data-add]')) {
      this.#addAndContinue(/** @type {HTMLButtonElement} */ (target.closest('[data-add]')));
    }
  };

  /** @param {HTMLButtonElement} button */
  async #addAndContinue(button) {
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    this.querySelector('[data-error]')?.setAttribute('hidden', '');

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [{ id: Number(this.dataset.variantId), quantity: 1 }] }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Add failed');
      // The added product can unlock a bonus build (assets/wt-free-gift.js): update the gift first.
      const freeGift = /** @type {any} */ (document.querySelector('wt-free-gift'));
      await freeGift?.sync?.().catch(() => {});
      this.dialog?.close();
      this.pendingButton = null;
      window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`;
    } catch (error) {
      this.querySelector('[data-error]')?.removeAttribute('hidden');
      button.removeAttribute('aria-busy');
    }
  }

  /** Re-trigger the original checkout action, skipping this interceptor once. */
  #continue() {
    const button = this.pendingButton;
    this.pendingButton = null;
    if (!button || !button.isConnected) {
      window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`;
      return;
    }
    this.bypass = true;
    try {
      button.click();
    } finally {
      this.bypass = false;
    }
  }
}

if (!customElements.get('wt-checkout-upsell')) {
  customElements.define('wt-checkout-upsell', WtCheckoutUpsell);
}
